import { useCallback, useEffect, useMemo, useState } from "react";
import { SettingOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { canAssignWfhToEmployee, isAdminUser } from "../../../app/utils/common/Functions";
import {
    canManageLeaveBalances,
    getLeaveManagementTabFromPath,
    getLeaveManagementTabPath,
    LEAVE_MANAGEMENT_TABS,
} from "../../../app/utils/leave/leaveManagementTabs";
import { LeaveApproveList } from "../../molecules/LeaveApproveList/LeaveApproveList";
import LeaveRequestStats from "../../molecules/LeaveRequestStats/LeaveRequestStats";
import LeaveNotificationSettings from "../../molecules/LeaveNotificationSettings/LeaveNotificationSettings";
import LeaveBalanceAdminPanel from "../../molecules/LeaveBalanceAdminPanel/LeaveBalanceAdminPanel";
import HrCreateWfhDrawer from "../../molecules/HrCreateWfhDrawer/HrCreateWfhDrawer";
import AddButton from "../../atoms/AddButton/AddButton";
import "./LeaveApprove.scss";

const LeaveApprove = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useSelector((state) => state.auth.auth);
    const { statusCounts, loading } = useSelector((state) => state.leaveAprrove);
    const isAdmin = isAdminUser(user);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("requests");
    const [wfhDrawerOpen, setWfhDrawerOpen] = useState(false);
    const canManageBalances = canManageLeaveBalances(user);
    const canAssignWfh = canAssignWfhToEmployee();

    const visibleTabs = useMemo(
        () => LEAVE_MANAGEMENT_TABS.filter((tab) => !tab.hrOrAdminOnly || canManageBalances),
        [canManageBalances]
    );

    const visibleTabKeys = useMemo(
        () => visibleTabs.map((tab) => tab.key),
        [visibleTabs]
    );

    const pathTab = useMemo(
        () => getLeaveManagementTabFromPath(location.pathname, visibleTabKeys),
        [location.pathname, visibleTabKeys]
    );

    useEffect(() => {
        setActiveTab(pathTab);
    }, [pathTab]);

    useEffect(() => {
        if ((pathTab === "balances" || pathTab === "ex-balances") && !canManageBalances) {
            navigate(getLeaveManagementTabPath("requests"), { replace: true });
        }
    }, [pathTab, canManageBalances, navigate]);

    const handleTabChange = useCallback((tabKey) => {
        navigate(getLeaveManagementTabPath(tabKey));
    }, [navigate]);

    return (
        <div className="leave-management-page leave-approve-page">
            <header className="leave-management-page__hero">
                <div className="leave-management-page__hero-main">
                    <div className="leave-management-page__intro-header">
                        <h1 className="page-title">Leave Management</h1>
                        {isAdmin && (
                            <button
                                type="button"
                                onClick={() => setSettingsOpen((open) => !open)}
                                aria-expanded={settingsOpen}
                                aria-label="Leave notification settings"
                                className={`leave-management-page__settings-toggle${settingsOpen
                                        ? " leave-management-page__settings-toggle--active"
                                        : ""
                                    }`}
                            >
                                <SettingOutlined />
                            </button>
                        )}
                    </div>
                    <p className="page-subtitle">
                        Review and act on incoming leave requests efficiently.
                    </p>
                </div>
                {canAssignWfh && activeTab === "requests" ? (
                    <div className="leave-management-page__hero-actions">
                        <AddButton
                            buttonTitle="Assign WFH"
                            image="/svg/leave.svg"
                            onClick={() => setWfhDrawerOpen(true)}
                        />
                    </div>
                ) : null}
            </header>
            <LeaveNotificationSettings open={settingsOpen} />
            <LeaveRequestStats statusCounts={statusCounts} loading={loading} />
            {canManageBalances ? (
                <div className="leave-approve-page__tabs">
                    {visibleTabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className={activeTab === tab.key ? "is-active" : ""}
                            onClick={() => handleTabChange(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            ) : null}
            {activeTab === "balances" && canManageBalances ? (
                <LeaveBalanceAdminPanel variant="active" />
            ) : activeTab === "ex-balances" && canManageBalances ? (
                <LeaveBalanceAdminPanel variant="ex" />
            ) : (
                <LeaveApproveList />
            )}
            <HrCreateWfhDrawer isOpen={wfhDrawerOpen} onClose={() => setWfhDrawerOpen(false)} />
        </div>
    );
};

export default LeaveApprove;